/**
 * Kontoschlüssel einrichten und Geräte entsperren (E21, 25.10.2026).
 *
 * Face ID über den Passkey des Kontos (WebAuthn-Erweiterung PRF — am iPhone
 * gemessen: Safari und App liefern stabil denselben Wert), Ausweg ist der
 * Wiederherstellungscode. Der Server sieht nur Verpackungen (`/keys`).
 */

import { apiFetch, ApiError } from '../api.js';
import { currentOwnerId } from '../auth.js';
import { db } from '../db/local.js';
import type { KraftwerkDatabase } from '../db/local.js';
import { markTablesResealed, openHeldRows, resealAll } from '../sync/e2e.js';
import { ENCRYPTED_TABLES } from '@kraftwerk/shared';
import { resetHouseholdKeyTimer } from './householdKeys.js';
import type { UnlockedBy } from './e2e.js';
import {
  fromB64,
  newAccountKey,
  newRecoveryCode,
  parseRecoveryCode,
  randomBytes,
  readRawAccountKey,
  storeAccountKey,
  writeUnlockedBy,
  toB64,
  unwrapAccountKey,
  wrapAccountKey,
} from './e2e.js';

interface ServerWrap {
  kind: 'passkey' | 'recovery';
  credentialId: string | null;
  salt: string;
  wrapped: string;
}

export type KeyError =
  | 'cancelled'
  | 'noPrf'
  | 'otherAccount'
  | 'notVerified'
  | 'unknownPasskey'
  | 'wrongCode'
  | 'exists'
  | 'offline';

export class KeySetupError extends Error {
  constructor(readonly reason: KeyError) {
    super(reason);
  }
}

/** Bit 2 der Flags (Byte 32 der Authenticator-Daten): Nutzer verifiziert. */
const USER_VERIFIED = 0x04;

interface PrfResult {
  credentialId: string;
  prf: Uint8Array<ArrayBuffer>;
}

/**
 * Face ID und PRF-Wert. Ohne `known` darf jeder Passkey des Kontos antworten
 * (Einrichten); mit `known` nur die schon verpackten, je mit ihrem Salt.
 */
async function passkeyPrf(
  known: readonly { credentialId: string; salt: string }[] | null,
  salt: Uint8Array<ArrayBuffer> | null,
): Promise<PrfResult> {
  const prf = known
    ? {
        evalByCredential: Object.fromEntries(
          known.map((wrap) => [wrap.credentialId, { first: fromB64(wrap.salt) }]),
        ),
      }
    : { eval: { first: salt! } };
  let credential: PublicKeyCredential;
  try {
    credential = (await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32),
        rpId: location.hostname,
        userVerification: 'required',
        allowCredentials: known
          ? known.map((wrap) => ({ type: 'public-key' as const, id: fromB64(wrap.credentialId) }))
          : [],
        extensions: { prf } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential;
  } catch {
    throw new KeySetupError('cancelled');
  }
  const response = credential.response as AuthenticatorAssertionResponse;
  const flags = new Uint8Array(response.authenticatorData)[32] ?? 0;
  if ((flags & USER_VERIFIED) === 0) throw new KeySetupError('notVerified');
  const owner = response.userHandle
    ? new TextDecoder().decode(new Uint8Array(response.userHandle))
    : null;
  if (!owner || owner !== currentOwnerId()) throw new KeySetupError('otherAccount');
  const results = (
    credential.getClientExtensionResults() as {
      prf?: { results?: { first?: ArrayBuffer } };
    }
  ).prf?.results?.first;
  if (!results) throw new KeySetupError('noPrf');
  return { credentialId: toB64(new Uint8Array(credential.rawId)), prf: new Uint8Array(results) };
}

async function fetchWraps(): Promise<ServerWrap[]> {
  try {
    return (await apiFetch<{ keys: ServerWrap[] }>('/keys')).keys;
  } catch {
    throw new KeySetupError('offline');
  }
}

export interface PendingSetup {
  /** Einmal zeigen — danach kennt ihn niemand mehr, auch der Server nicht. */
  code: string;
  /** Erst nach „Code gesichert“: hochladen, merken, Bestand verschlüsseln. */
  finish: () => Promise<void>;
}

/** Schritt 1: Face ID, Schlüssel und Code entstehen — noch nichts gespeichert. */
export async function beginSetup(database: KraftwerkDatabase = db): Promise<PendingSetup> {
  const salt = randomBytes(32);
  const { credentialId, prf } = await passkeyPrf(null, salt);
  const accountKey = newAccountKey();
  const code = newRecoveryCode();
  const passkey = { credentialId, ...(await wrapAccountKey(accountKey, prf, 'passkey', salt)) };
  const recovery = await wrapAccountKey(accountKey, parseRecoveryCode(code)!, 'recovery');

  return {
    code,
    finish: async () => {
      try {
        await apiFetch('/keys/setup', {
          method: 'POST',
          body: JSON.stringify({ passkey, recovery }),
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) throw new KeySetupError('exists');
        throw new KeySetupError('offline');
      }
      await storeAccountKey(accountKey, database);
      await writeUnlockedBy('passkey', database);
      await resealAll(database);
      await markTablesResealed(ENCRYPTED_TABLES, database);
      resetHouseholdKeyTimer();
    },
  };
}

async function unlocked(raw: Uint8Array<ArrayBuffer>, by: UnlockedBy, database: KraftwerkDatabase) {
  await storeAccountKey(raw, database);
  await writeUnlockedBy(by, database);
  await openHeldRows(database);
  resetHouseholdKeyTimer();
}

/** Entsperren mit Face ID — mit einem Passkey, der schon verpackt ist. */
export async function unlockWithPasskey(database: KraftwerkDatabase = db): Promise<void> {
  const wraps = (await fetchWraps()).filter(
    (wrap): wrap is ServerWrap & { credentialId: string } =>
      wrap.kind === 'passkey' && wrap.credentialId !== null,
  );
  if (wraps.length === 0) throw new KeySetupError('unknownPasskey');
  const { credentialId, prf } = await passkeyPrf(wraps, null);
  const wrap = wraps.find((candidate) => candidate.credentialId === credentialId);
  if (!wrap) throw new KeySetupError('unknownPasskey');
  await unlocked(await unwrapAccountKey(wrap, prf, 'passkey'), 'passkey', database);
}

/** Entsperren mit dem Wiederherstellungscode. */
export async function unlockWithCode(
  input: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const secret = parseRecoveryCode(input);
  if (!secret) throw new KeySetupError('wrongCode');
  const wrap = (await fetchWraps()).find((candidate) => candidate.kind === 'recovery');
  if (!wrap) throw new KeySetupError('wrongCode');
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = await unwrapAccountKey(wrap, secret, 'recovery');
  } catch {
    throw new KeySetupError('wrongCode');
  }
  await unlocked(raw, 'code', database);
}

/**
 * Face ID für dieses Gerät dazunehmen (nach dem Entsperren mit Code): den
 * Kontoschlüssel zusätzlich mit diesem Passkey verpacken.
 */
export async function addThisPasskey(database: KraftwerkDatabase = db): Promise<void> {
  const raw = await readRawAccountKey(database);
  if (!raw) throw new KeySetupError('cancelled');
  const salt = randomBytes(32);
  const { credentialId, prf } = await passkeyPrf(null, salt);
  const wrap = await wrapAccountKey(raw, prf, 'passkey', salt);
  try {
    await apiFetch('/keys/passkey', {
      method: 'POST',
      body: JSON.stringify({ credentialId, ...wrap }),
    });
  } catch {
    throw new KeySetupError('offline');
  }
  await writeUnlockedBy('passkey', database);
}
